import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { syncEngine } from '@services/offline/syncEngine';
import { updateServiceWorker } from '@services/pwa/updateController';
import { triggerForcedUpdateBackup } from '@services/offline/forcedUpdateBackup';
import { subscribeToCompatibility, subscribeToHealthy } from '@/api/appCompatibility';
import type { ForceUpdateReason } from '../types';
import { ForceUpdateScreen } from '../components/ForceUpdateScreen';
import { UpdateRecoveryScreen } from '../components/UpdateRecoveryScreen';
import { useUpdateAttemptCounter } from '../hooks/useUpdateAttemptCounter';
import { AppCompatibilityContext, type MaintenanceState } from './AppCompatibilityContextValue';

/**
 * Owns the global "the backend told us to stop" state for phases C.5/C.6. The
 * HTTP client cannot touch React, so it dispatches a `CompatibilitySignal` DOM
 * event (mirroring the existing `authSessionExpiredEvent` flow); this provider is
 * the single subscriber and the single owner of the resulting UI + side effects:
 *
 *  - force-update (426): latch a non-dismissible <ForceUpdateScreen/>. It never
 *    clears on its own — the user must actually update. The action runs the
 *    forced-update flow (C.6): back up the sync queue synchronously (C.7 seam),
 *    record the attempt, then apply the waiting service worker.
 *  - maintenance (423/503): pause the sync queue and publish the state via
 *    `AppCompatibilityContext`. The provider no longer renders the banner itself;
 *    AppLayout reads `useMaintenanceState()` and renders the sticky
 *    <MaintenanceBanner/> under its header, so the notice stays visible on scroll
 *    (regulatory requirement — see the banner's JSDoc). It self-heals: it clears
 *    on the next healthy response, or after `Retry-After`.
 *
 * Stuck-update recovery (C.6): each forced-update attempt is recorded; a healthy
 * response after the reload clears the history. If updates keep failing to take
 * (>= threshold within the window) we swap the force screen for the manual
 * <UpdateRecoveryScreen/> instead of letting the user keep looping.
 *
 * The sync queue is paused whenever *either* condition is active — a superset of
 * the brief (which named sync-schema/423/503): pushing writes against a backend
 * that is force-blocking this client or in maintenance only produces error noise.
 */
export const AppCompatibilityProvider = ({ children }: PropsWithChildren) => {
  const [forceUpdate, setForceUpdate] = useState<ForceUpdateReason | null>(null);
  const [maintenance, setMaintenance] = useState<MaintenanceState | null>(null);
  const [updating, setUpdating] = useState(false);
  const { recoveryActive, record, reset } = useUpdateAttemptCounter();

  // Subscribe to the interceptor's signals once, for the life of the app.
  useEffect(
    () =>
      subscribeToCompatibility((signal) => {
        if (signal.kind === 'force-update') {
          setForceUpdate(signal.reason);
        } else {
          setMaintenance({ level: signal.level, retryAfterSeconds: signal.retryAfterSeconds });
        }
      }),
    []
  );

  // A healthy 2xx after a reload proves the new build works: clear the stuck-update
  // attempt history so the recovery screen does not show on the next force signal.
  useEffect(() => subscribeToHealthy(reset), [reset]);

  // Pause the sync queue whenever a blocking condition is active; resume once both clear.
  useEffect(() => {
    if (forceUpdate !== null || maintenance !== null) {
      syncEngine.pause();
    } else {
      syncEngine.resume();
    }
  }, [forceUpdate, maintenance]);

  // Self-healing for maintenance: clear on the next healthy 2xx, or after Retry-After.
  useEffect(() => {
    if (!maintenance) {
      return;
    }

    const clear = () => setMaintenance(null);
    const unsubscribe = subscribeToHealthy(clear);

    let timer: ReturnType<typeof setTimeout> | undefined;
    if (maintenance.retryAfterSeconds && maintenance.retryAfterSeconds > 0) {
      timer = setTimeout(clear, maintenance.retryAfterSeconds * 1000);
    }

    return () => {
      unsubscribe();
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [maintenance]);

  const handleForcedUpdate = useCallback(() => {
    // Synchronous, inside the gesture, before any await: back up the queue (C.7
    // seam) and count the attempt. The reload after `updateServiceWorker` persists
    // both via localStorage / the download, so ordering here is what matters.
    triggerForcedUpdateBackup();
    record();
    setUpdating(true);
    void updateServiceWorker(true);
  }, [record]);

  // Manual recovery: give the waiting worker one more chance (no new attempt is
  // counted — this is the escape hatch, not another loop iteration).
  const handleRecoveryReload = useCallback(() => {
    void updateServiceWorker(true);
  }, []);

  const renderForceBlock = () => {
    if (forceUpdate === null) {
      return null;
    }

    return recoveryActive ? (
      <UpdateRecoveryScreen onReload={handleRecoveryReload} />
    ) : (
      <ForceUpdateScreen reason={forceUpdate} onUpdate={handleForcedUpdate} updating={updating} />
    );
  };

  const contextValue = useMemo(() => ({ maintenance }), [maintenance]);

  return (
    <AppCompatibilityContext.Provider value={contextValue}>
      {children}
      {renderForceBlock()}
    </AppCompatibilityContext.Provider>
  );
};
